"use client";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./map.module.scss";

import { GoogleMap, Marker, StandaloneSearchBox } from "@react-google-maps/api";
import { Button } from "Components/CustomComponents";
import { InfoToast } from "Hooks/useToast";
import _ from "lodash";
import { geocodeByLatLng } from "react-google-places-autocomplete";

type LocationType = {
  lat: number;
  lng: number;
};

interface MapContainerProps {
  location: Partial<LocationType>;
  setLatLng: (latlng: LocationType) => void;
  address?: string;
  setAddress?: (add: string) => void;
  setClose?: (close: boolean) => void;
  option?: Partial<object>;
  className?: string;
  zoom?: number;
  isEdit?: boolean;
  searchBar?: boolean;
}

const Map: React.FC<MapContainerProps> = ({
  location,
  setLatLng = () => { },
  address = "",
  setAddress = () => { },
  option = {},
  zoom = 9,
  className = "",
  searchBar = false,
  isEdit = false,
  setClose = () => { },
}) => {
  const componentWillUnmount = useRef(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [searchBox, setSearchBox] =
    useState<google.maps.places.SearchBox | null>(null);
  const [center, setCenter] = useState({ lat: 32.7767, lng: -97.030821 });
  const options = useMemo(
    () => ({
      mapId: "17c975ad78fa8aef",
      disableDefaultUI: true,
      clickableIcons: false,
      ...option,
    }),
    []
  );

  const [position, setPosition] = useState(location);
  const [add, setAdd] = useState(address);

  useEffect(() => {
    if (!location || (!location.lat && !location.lng)) {
      getCurrentLocation();
    }

    return () => {
      componentWillUnmount.current = true;
    };
  }, []);

  useEffect(() => {
    (async () => {
      if (
        !mapRef.current ||
        _.isEmpty(position) ||
        position.lat == null || // Ensures lat is neither null nor undefined
        position.lng == null
      ) {
        return;
      }

      await mapRef.current.panTo({ lat: position.lat, lng: position.lng });
      // mapRef.current?.setZoom(14);
    })();

    return () => {
      if (componentWillUnmount.current) {
        if (position.lat == null && position.lng == null) {
          setLatLng({
            lat: position.lat as unknown as number,
            lng: position.lng as unknown as number,
          });
          setAddress(add);
        }
      }
    };
  }, [position]);

  const getCurrentLocation = useCallback(() => {
    navigator.geolocation.getCurrentPosition(
      async function (position) {
        const { latitude, longitude } = position.coords;
        try {
          const places = await geocodeByLatLng({
            lat: latitude,
            lng: longitude,
          });
          if (_.isEmpty(places)) {
            return InfoToast("Unable to find address for this location");
          }
          setAdd(places[0].formatted_address);
          setPosition({
            lat: latitude,
            lng: longitude,
          });
          await mapRef?.current?.panTo({ lat: latitude, lng: longitude });
        } catch (err) {
          console.log(err)
          InfoToast("Unable to find address for this location");
        }
      },
      function () {
        InfoToast("Site Not Able To Access Your location !");
      }
    );
  }, []);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    if (location) {
      mapRef.current.setZoom(14);
    }
  }, []);

  const onClick = async (e: google.maps.MapMouseEvent) => {
    if (!isEdit || !e.latLng) return;

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    try {
      const places = await geocodeByLatLng({ lat, lng });
      if (_.isEmpty(places)) {
        return InfoToast("Unable to find address for this location");
      }

      setPosition({ lat, lng });
      setAdd(places[0].formatted_address);
    } catch (err) {
      console.log(err);
      InfoToast("Unable to find address for this location");
    }
  };

  const handleLoad = (ref: google.maps.places.SearchBox) => {
    setSearchBox(ref);
  };

  const handlePlaceChange = async () => {
    if (!searchBox) return;
    const places = searchBox.getPlaces();
    if (!places || places.length === 0) return;

    const place = places[0];
    const { lat, lng } = place.geometry!.location!; // Fixed Type Assertion
    setAdd(place?.formatted_address ?? "");
    setPosition({
      lat: lat(),
      lng: lng(),
    });
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAdd(e.target.value);
    if (position.lat != null && position.lng != null) {
      setCenter({ lat: position.lat, lng: position.lng });
      setPosition({});
    }
  };

  const saveLocation = () => {
    if (position.lat != null && position.lng != null) {
      setLatLng({ lat: position.lat, lng: position.lng });
      setAddress(add);
    }
    setClose(false);
  };


  return (
    <div className="w-100 h-100 d-flex flex-column">
      <GoogleMap
        zoom={zoom}
        center={
          position.lat != null && position.lng != null
            ? { lat: position.lat, lng: position.lng }
            : center
        }
        mapContainerClassName={`${styles.googleMap} ${className}`}
        options={options}
        onLoad={onLoad}
        onClick={onClick}
      >
        {position.lat != null && position.lng != null && (
          <Marker position={{ lat: position.lat, lng: position.lng }} />
        )}
        {!searchBar ? (
          ""
        ) : (
          <StandaloneSearchBox
            onPlacesChanged={handlePlaceChange}
            onLoad={handleLoad}
          >
            <input
              type="text"
              placeholder="Search your address here"
              value={add}
              onChange={handleAddressChange}
              style={{
                boxSizing: "border-box",
                border: `1px solid transparent`,
                width: `70%`,
                maxWidth: `600px`,
                height: `45px`,
                padding: `0 12px`,
                borderRadius: `3px`,
                boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`,
                fontSize: `14px`,
                outline: `none`,
                margin: "auto",
                textOverflow: `ellipses`,
                position: "absolute",
                top: "60px",
                left: "50%",
                right: "50%",
                transform: "translate(-50%,-50%)",
              }}
            />
          </StandaloneSearchBox>
        )}
      </GoogleMap>
      <Button
        classes="px-4 mt-1 royalBlueBg"
        text="Save"
        onClick={saveLocation}
      />
    </div>
  );
};

export default React.memo(Map);
